import React, { useEffect, useMemo, useRef } from 'react';
import { ChevronUp } from '@gravity-ui/icons';
import {
  SCOPE_LEVELS,
  SCOPE_LEVEL_KEYS,
  clearScopeFrom,
  createEmptyScope,
  describeScopeSelection,
  isEmptyScope,
  normalizeScope,
  pruneScopeLevel,
  toggleScopeId,
} from '../../utils/scopeSelection';
import useScopeOptions, { PARENT_OF } from './useScopeOptions';
import './ScopeFilter.css';

/*
 * FILTER-UX / SCOPE-MULTI01 (ТЗ v4 §21–22, FE-FIX18/FE-FIX19).
 *
 * Один reusable cascading checkbox-фильтр: Объект → Подразделение → Область →
 * Локация → Камера → Правило. Компонент контролируемый и не знает, какой экран
 * его показывает: выбор и его хранение (URL, cache) — дело страницы, поэтому
 * «Сводка» и «Контроль» используют один и тот же компонент, но независимые
 * состояния.
 *
 * Пустой уровень означает «все доступные» — в запрос он не попадает вовсе, и
 * сам фронт никогда не подставляет все ID вместо отсутствия фильтра. Все ID
 * уровня появляются в выборе только по явному клику на «Все …»: иначе у
 * выбранных объектов не раскрыть подразделения.
 *
 * С `onApply` фильтр работает как черновик: `value`/`onChange` — выбор в
 * галочках, а расчёт страница запускает только по «Применить». Каждый клик
 * по чекбоксу иначе стоил тяжёлых запросов аналитики и подвешивал экран.
 */

const HIERARCHY_KEYS = SCOPE_LEVEL_KEYS.filter((key) => key !== 'ruleIds');

const LEVEL_HINT = {
  workshopIds: 'Выберите объект, чтобы уточнить',
  sectionIds: 'Выберите подразделение, чтобы уточнить',
  roomIds: 'Выберите область, чтобы уточнить',
  cameraIds: 'Выберите локацию, чтобы уточнить',
  zoneIds: 'Выберите камеру, чтобы уточнить',
};

const stateMessage = (levelKey, level) => {
  switch (level.state) {
    case 'loading':
      return 'Загрузка…';
    case 'error':
      return 'Не удалось загрузить список';
    case 'forbidden':
      return 'Список правил недоступен для вашей роли';
    case 'empty':
      return 'Нет доступных значений';
    case 'idle':
      return LEVEL_HINT[levelKey] || '';
    default:
      return '';
  }
};

/**
 * Выбранный ID, которого нет в загруженных опциях (пришёл из ссылки или
 * остался от прежней ветви), всё равно рисуется чекбоксом: скрытый активный
 * фильтр хуже некрасивой строки «#12». Имя берётся из уже виденных опций
 * уровня (`known`), «#12» — только если имени фронт не видел никогда.
 */
const buildRows = (options, selectedIds, known = {}) => {
  const loaded = new Set(options.map((option) => option.id));
  const orphans = selectedIds
    .filter((id) => !loaded.has(id))
    .map((id) => ({
      id,
      name: known[id]?.name || `#${id}`,
      parentName: known[id]?.parentName || '',
      orphan: true,
    }));
  return [...options, ...orphans];
};

/** indeterminate есть только у DOM-узла, атрибутом его не задать. */
export const TriStateCheckbox = ({ checked, indeterminate, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-checked={indeterminate ? 'mixed' : checked}
      {...props}
    />
  );
};

/*
 * FE-FIX16: `levels` можно передать снаружи. «Статистика» и «Ганта» уже держат
 * useScopeOptions у себя (им нужны разрешённые камеры, чтобы строить панели), и
 * без этой возможности компонент грузил бы те же уровни второй раз.
 */
const ScopeFilter = ({
  value,
  onChange,
  includeRules = false,
  // Категория правил для уровня «Правила»: 'violation' | 'business'.
  rulesKind = 'violation',
  includeZones = false,
  levels: injectedLevels = null,
  disabled = false,
  onApply = null,
  applyDisabled = true,
  idPrefix = 'scope',
  title = 'Область данных',
  children = null,
  // Страница, которая прячет фильтр за кнопкой, передаёт onCollapse — в шапке
  // появляется иконка «Свернуть».
  onCollapse = null,
  // Сброс всего фильтра страницы (включая поля в children). Без него
  // «Сбросить всё» очищает только уровни.
  onReset = null,
  resetDisabled = null,
  // Имена выбранных ID, которых нет в опциях: { cameraIds: { '7': 'Цех 1' } }.
  knownNames = null,
}) => {
  const scope = useMemo(() => normalizeScope(value), [value]);
  const ownLevels = useScopeOptions(scope, { includeRules, rulesKind, includeZones, enabled: !injectedLevels });
  const levels = injectedLevels || ownLevels;
  const visibleKeys = useMemo(() => SCOPE_LEVEL_KEYS.filter((key) => {
    if (key === 'ruleIds') return includeRules;
    if (key === 'zoneIds') return includeZones;
    return true;
  }), [includeRules, includeZones]);

  /*
   * Подрезка потомков после смены родителя. Подрезаем только когда список
   * опций уровня действительно получен целиком (ready/empty без partial): на
   * loading выбор ещё валиден, на idle уровень никем не сужен, а при partial
   * пункты упавшего родителя просто не загружены — выбрасывать их нельзя.
   *
   * Если подрезка опустошила уровень, ветви ниже очищаются так же, как при
   * снятии последнего родителя вручную: иначе после смены объекта нижние
   * уровни оставались выбранными без видимого родителя («Локация #19»).
   */
  useEffect(() => {
    let next = scope;
    Object.keys(PARENT_OF).forEach((levelKey) => {
      const level = levels[levelKey];
      if (level.state !== 'ready' && level.state !== 'empty') return;
      if (level.partial) return;
      const before = next[levelKey].length;
      next = pruneScopeLevel(next, levelKey, level.options.map((option) => option.id));
      const childIndex = HIERARCHY_KEYS.indexOf(levelKey) + 1;
      if (before > 0 && next[levelKey].length === 0 && childIndex > 0 && childIndex < HIERARCHY_KEYS.length) {
        next = { ...clearScopeFrom(next, HIERARCHY_KEYS[childIndex]), ruleIds: next.ruleIds };
      }
    });
    if (next !== scope) onChange(next);
  }, [levels, scope, onChange]);

  const handleToggle = (levelKey, id) => {
    let next = toggleScopeId(scope, levelKey, id);
    // Снятие последнего родителя очищает ветви ниже сразу: иначе фильтр остался
    // бы активным, но без видимого списка, из которого его можно снять.
    const childIndex = HIERARCHY_KEYS.indexOf(levelKey) + 1;
    if (next[levelKey].length === 0 && childIndex > 0 && childIndex < HIERARCHY_KEYS.length) {
      next = { ...clearScopeFrom(next, HIERARCHY_KEYS[childIndex]), ruleIds: next.ruleIds };
    }
    onChange(next);
  };

  const resetAll = () => (onReset ? onReset() : onChange(createEmptyScope()));
  const resetBlocked = resetDisabled === null ? isEmptyScope(scope) : resetDisabled;

  const resetLevel = (levelKey) => {
    const childIndex = HIERARCHY_KEYS.indexOf(levelKey);
    if (childIndex >= 0) {
      onChange({ ...clearScopeFrom(scope, levelKey), ruleIds: scope.ruleIds });
      return;
    }
    onChange({ ...scope, [levelKey]: [] });
  };

  /*
   * «Все …» — tri-state: отмечен, когда выбраны все загруженные пункты уровня,
   * промежуточный — когда часть. Клик добирает недостающие пункты (у каждого
   * выбранного родителя раскрываются потомки), а при полном выборе снимает
   * уровень вместе с ветвями ниже — как снятие последнего пункта вручную.
   */
  const toggleAll = (levelKey, optionIds, allSelected) => {
    if (allSelected) {
      resetLevel(levelKey);
      return;
    }
    const selected = scope[levelKey];
    onChange({ ...scope, [levelKey]: [...selected, ...optionIds.filter((id) => !selected.includes(id))] });
  };

  const renderRows = (levelKey, definition, rows, selected) => {
    const optionIds = rows.filter((row) => !row.orphan).map((row) => row.id);
    const selectedCount = optionIds.filter((id) => selected.includes(id)).length;
    const allSelected = optionIds.length > 0 && selectedCount === optionIds.length;

    return (
      <div className="scope-level-list">
        {optionIds.length > 0 && (
          <label className="scope-option scope-option--all">
            <TriStateCheckbox
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              disabled={disabled}
              id={`${idPrefix}-all-${levelKey}`}
              onChange={() => toggleAll(levelKey, optionIds, allSelected)}
            />
            <span className="scope-option-text">
              <span className="scope-option-name">{definition.labels.all}</span>
            </span>
          </label>
        )}
        {rows.map((option) => (
          <label
            key={option.id}
            className="scope-option"
            title={option.parentName ? `${option.name} — ${option.parentName}` : option.name}
          >
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              disabled={disabled}
              onChange={() => handleToggle(levelKey, option.id)}
            />
            <span className="scope-option-text">
              <span className="scope-option-name">{option.name}</span>
              {option.parentName && <span className="scope-option-parent">{option.parentName}</span>}
            </span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <section className="scope-filter" aria-label={title}>
      <div className="scope-filter-head">
        <span className="scope-filter-title">{title}</span>
        <span className="scope-filter-summary">{describeScopeSelection(scope)}</span>
        {onApply && (
          <button
            type="button"
            className="scope-filter-apply"
            disabled={disabled || applyDisabled}
            onClick={onApply}
          >
            Применить
          </button>
        )}
        <button
          type="button"
          className="scope-filter-reset"
          disabled={disabled || resetBlocked}
          onClick={resetAll}
        >
          Сбросить всё
        </button>
        {onCollapse && (
          <button
            type="button"
            className="scope-filter-collapse"
            onClick={onCollapse}
            aria-label="Свернуть фильтр"
            title="Свернуть фильтр"
          >
            <ChevronUp width={16} height={16} aria-hidden />
          </button>
        )}
      </div>

      <div className="scope-filter-levels" style={{ '--scope-levels': visibleKeys.length }}>
        {visibleKeys.map((levelKey) => {
          const definition = SCOPE_LEVELS.find((item) => item.key === levelKey);
          const level = levels[levelKey];
          const selected = scope[levelKey];
          const extraNames = knownNames?.[levelKey];
          const known = extraNames
            ? {
              ...Object.fromEntries(Object.entries(extraNames).map(([id, name]) => [id, { name }])),
              ...(level.known || {}),
            }
            : level.known;
          const rows = buildRows(level.options, selected, known);
          const message = stateMessage(levelKey, level);
          const retry = level.state === 'error' && (
            <button type="button" className="scope-level-retry" onClick={level.reload}>
              Повторить
            </button>
          );

          return (
            <fieldset key={levelKey} className="scope-level">
              <legend className="scope-level-head">
                <span className="scope-level-label">{definition.labels.plural}</span>
                <span className={selected.length ? 'scope-level-count scope-level-count--active' : 'scope-level-count'}>
                  {selected.length === 0 ? 'все' : `выбрано ${selected.length}`}
                </span>
              </legend>

              {rows.length > 0
                ? renderRows(levelKey, definition, rows, selected)
                : (
                  <div className="scope-level-placeholder">
                    {message && <p className="scope-level-note">{message}</p>}
                    {retry}
                  </div>
                )}

              {rows.length > 0 && message && (
                <p className="scope-level-note">{message}{retry}</p>
              )}
              {level.partial && level.state !== 'loading' && (
                <p className="scope-level-note scope-level-note--warn">
                  Часть списка не загрузилась — выбор может быть неполным
                  <button type="button" className="scope-level-retry" onClick={level.reload}>
                    Повторить
                  </button>
                </p>
              )}
            </fieldset>
          );
        })}
      </div>

      {/* Поля страницы (период и т.п.), которые применяются той же кнопкой. */}
      {children && <div className="scope-filter-extra">{children}</div>}
    </section>
  );
};

export default ScopeFilter;
